/**
 * Small helpers for Media v2 — month grouping, label formatting.
 * Kept dependency-free: native Date + Intl, no date-fns / dayjs.
 */

/** Return YYYY-MM bucket key for a createdAt timestamp, or 'undated'. */
export const monthBucket = (createdAt) => {
  if (!createdAt) return 'undated';
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return 'undated';
  const d = new Date(t);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});

/** Human label like "April 2026" for a YYYY-MM key. */
export const monthLabel = (key) => {
  if (!key || key === 'undated') return 'Undated';
  const [y, m] = key.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return key;
  // Day 15 avoids any TZ-edge drift
  return MONTH_FORMATTER.format(new Date(y, m - 1, 15));
};

/**
 * Group items by month bucket. Returns an ordered array of
 * { key, label, items } descending by bucket key so newest month is first.
 * Within each month, items are sorted by createdAt desc.
 */
export const groupByMonth = (items) => {
  const by = new Map();
  for (const item of items) {
    const key = monthBucket(item?.createdAt);
    if (!by.has(key)) by.set(key, []);
    by.get(key).push(item);
  }
  const keys = Array.from(by.keys()).sort((a, b) => {
    if (a === 'undated') return 1;
    if (b === 'undated') return -1;
    return a < b ? 1 : a > b ? -1 : 0;
  });
  return keys.map((key) => {
    const bucketItems = [...by.get(key)].sort((x, y) => {
      const tx = Date.parse(x?.createdAt || 0) || 0;
      const ty = Date.parse(y?.createdAt || 0) || 0;
      return ty - tx;
    });
    return { key, label: monthLabel(key), items: bucketItems };
  });
};

/** Feature-flag check. Default ON; set VITE_FEATURE_MEDIA_V2=false to disable. */
export const isMediaV2Enabled = () => {
  const flag = import.meta.env.VITE_FEATURE_MEDIA_V2;
  return flag !== 'false';
};

/**
 * Map the filter-pill choice to the actual section the backend stores.
 * WebsiteContentUploader has legacy sections (gallery/video/brochure); v2 tabs
 * use the user-facing trio (images/videos/brochures). We keep this bridge in
 * one place so MediaV2Shell's tabs stay consistent.
 */
export const filterToSection = (filter) => {
  if (filter === 'videos') return 'video';
  if (filter === 'brochures') return 'brochure';
  return 'gallery';
};
